import React, { useEffect, useRef, useState } from "react";
import * as d3 from 'd3';
import { CONFIG } from "../config";

const WeeklyDowntimeGraph = ({ selectedDate, selectedStatus, selectedAgeRange }) => {
  const containerRef = useRef();
  const svgRef = useRef();

  const [data, setData] = useState([]);
  const [downtime_percentage, setDownTimePercentage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const restrictedIds = JSON.parse(process.env.REACT_APP_RESTRICTED_USER_ID || '[]');
  const userId = parseInt(localStorage.getItem("tensor-user-id"), 10);
  const isRestricted = restrictedIds.includes(userId);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const { width } = entry.contentRect;
      setDimensions({ width, height: 120 });
    });

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!selectedDate) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${CONFIG.API_BASE_URL}/${CONFIG.DOWNTIME_NEXT_WEEK_PREDICTION_GRAPH_URL}/${localStorage.getItem(
          "tensor-user-id"
        )}?date_str=${selectedDate}&selected_status=${selectedStatus}&selected_agerange=${selectedAgeRange}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("tensor-auth-token"),
          },
        });

        const result = await response.json();
        if (result.success) {
          setData(result.data.dates_wise);
          setDownTimePercentage(result.data.downtime_percentage);
        } else {
          setData([]);
        }
      } catch (error) {
        console.error("Failed to fetch failures prevented data:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDate, selectedStatus, selectedAgeRange]);

  const thresholdColors = [
    { min: 30, color: 'red' },
    { min: 20, color: 'orange' },
    { min: 10, color: 'green' },
    { min: 0, color: 'black' },
  ];

  const getThresholdColor = (val) => {
    return thresholdColors.find(t => val >= t.min)?.color || 'gray';
  };

  useEffect(() => {
    const container = svgRef.current?.parentElement;
    if (!container) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const margin = { top: 25, right: 56, bottom: 30, left: 40 };
    let width = container.clientWidth;
    const height = 130;

    const parse = d3.timeParse('%m/%d');
    const format = d3.timeFormat('%m/%d');

    const processedData = data.map((d) => ({
      ...d,
      dateObj: parse(d.date),
    }));

    const x = d3
      .scaleTime()
      .domain(d3.extent(processedData, (d) => d.dateObj))
      .range([margin.left, width - margin.right]);

    const maxValue = Math.max(...processedData.map((d) => d.value));
    let yMax = 30;
    const step = 10;
    while (maxValue > yMax) yMax += step;
    const yTicks = d3.range(0, yMax + step, step);

    const y = d3
      .scaleLinear()
      .domain([0, yMax])
      .range([height - margin.bottom, margin.top]);

    const line = d3
      .line()
      .x((d) => x(d.dateObj))
      .y((d) => y(d.value))
      .curve(d3.curveMonotoneX);

    const resizeObserver = new ResizeObserver(() => {
      width = container.clientWidth;
      draw();
    });
    resizeObserver.observe(container);

    const draw = () => {
      svg.selectAll('*').remove();

      const startDate = d3.timeSunday.floor(d3.min(processedData, d => d.dateObj));
      const endDate = d3.max(processedData, d => d.dateObj);
      const weekTicks = d3.timeSunday.range(startDate, d3.timeSunday.ceil(endDate));

      const xAxisGroup = svg
        .append('g')
        .attr('transform', `translate(0,${height - margin.bottom + 10})`)
        .call(
          d3.axisBottom(x)
            .tickValues(weekTicks)
            .tickFormat(d => d3.timeFormat('%m/%d')(d3.timeDay.offset(d, -2)))
            .tickSize(0)
        );

      xAxisGroup.select('.domain').remove();
      xAxisGroup.selectAll('.tick line').remove();

      // ✅ Safe fix to hide first tick if it overflows
      const svgNode = d3.select(svgRef.current).node();
      if (svgNode) {
        const svgX = svgNode.getBoundingClientRect().x;
        const TICK_HIDE_PADDING = 30;

        xAxisGroup.selectAll('.tick text')
          .attr('font-size', '10px')
          .each(function(d, i) {
            const tick = d3.select(this);
            const tickNode = this;
            const tickX = tickNode.getBoundingClientRect().x;

            if (i === 0 && (tickX - svgX) < TICK_HIDE_PADDING) {
              tick.style('display', 'none');
            }
          });
      }

      svg
        .append('g')
        .attr('transform', `translate(${margin.left},0)`)
        .call(
          d3.axisLeft(y)
            .tickValues(yTicks)
            .tickFormat((d) => `${d}%`)
            .tickSize(0)
        )
        .call((g) => g.select('.domain').remove())
        .call((g) => g.selectAll('.tick line').remove());

      yTicks.forEach((val) => {
        const color = getThresholdColor(val);
        svg
          .append('line')
          .attr('x1', margin.left)
          .attr('x2', width - margin.right)
          .attr('y1', y(val))
          .attr('y2', y(val))
          .attr('stroke', color)
          .attr('stroke-width', 0.5)
          .attr('stroke-dasharray', val === 0 ? null : '2 2');
      });

      if (selectedDate) {
        const selectedDateObj = d3.timeParse('%m/%d/%Y')(selectedDate);
        const selectedDateFormatted = d3.timeFormat('%m/%d')(selectedDateObj);
        const todayFormatted = d3.timeFormat('%m/%d')(new Date());

        if (selectedDateFormatted !== todayFormatted) {
          const selected = processedData.find(d => d.date === selectedDateFormatted);
          if (selected) {
            const selectedX = x(selected.dateObj);
            svg
              .append('line')
              .attr('x1', selectedX)
              .attr('x2', selectedX)
              .attr('y1', margin.top)
              .attr('y2', height - margin.bottom)
              .attr("stroke", "#032FCE")
              .attr("stroke-dasharray", "4")
              .attr("stroke-width", 1);

            svg
              .append('text')
              .attr('x', selectedX)
              .attr('y', margin.top - 5)
              .attr('text-anchor', 'middle')
              .attr("fill", "#000000b3")
              .attr('font-size', '12px')
              .text(selectedDateFormatted);
          }
        }

        const oneWeekLaterDateObj = d3.timeDay.offset(selectedDateObj, 7);
        const oneWeekLaterFormatted = d3.timeFormat('%m/%d')(oneWeekLaterDateObj);

        const oneWeekLater = processedData.find(d => d.date === oneWeekLaterFormatted);

        if (oneWeekLater) {
          const oneWeekLaterX = x(oneWeekLater.dateObj);

          svg
            .append('line')
            .attr('x1', oneWeekLaterX)
            .attr('x2', oneWeekLaterX)
            .attr('y1', margin.top)
            .attr('y2', height - margin.bottom)
            .attr('stroke', '#FF5733')
            .attr('stroke-dasharray', '4')
            .attr('stroke-width', 1);

          svg
            .append('text')
            .attr('x', oneWeekLaterX)
            .attr('y', margin.top - 5)
            .attr('text-anchor', 'middle')
            .attr('fill', '#000000b3')
            .attr('font-size', '12px')
            .text(oneWeekLaterFormatted);
        }
      }

      svg
        .append('path')
        .datum(processedData)
        .attr('fill', 'none')
        .attr('stroke', '#1f3dc3')
        .attr('stroke-width', 2)
        .attr('d', line);

      const tooltip = d3
        .select(container)
        .append('div')
        .attr('class', 'absolute bg-white shadow-md p-2 rounded text-sm border border-gray-200')
        .style('pointer-events', 'none')
        .style('opacity', 0);

      const hoverLine = svg
        .append('line')
        .attr('stroke', '#ccc')
        .attr('y1', margin.top)
        .attr('y2', height - margin.bottom)
        .style('opacity', 0);

      const hoverDot = svg
        .append('circle')
        .attr('r', 5)
        .attr('fill', '#1f3dc3')
        .style('opacity', 0);

      svg
        .append('rect')
        .attr('fill', 'transparent')
        .attr('x', margin.left)
        .attr('y', margin.top)
        .attr('width', width - margin.left - margin.right)
        .attr('height', height - margin.top - margin.bottom)
        .on('mousemove', function (event) {
          const [mouseX] = d3.pointer(event);
          const closest = d3.least(
            processedData,
            (d) => Math.abs(x(d.dateObj) - mouseX)
          );
          if (closest) {
            const xCoord = x(closest.dateObj);
            const yCoord = y(closest.value);

            tooltip
              .style('opacity', 1)
              .html(`<strong>${closest.date}</strong><br/>${closest.value}%`)
              .style('left', event.pageX + 15 + 'px')
              .style('top', event.pageY - 40 + 'px');

            hoverLine
              .attr('x1', xCoord)
              .attr('x2', xCoord)
              .style('opacity', 1);

            hoverDot
              .attr('cx', xCoord)
              .attr('cy', yCoord)
              .style('opacity', 1);
          }
        })
        .on('mouseleave', () => {
          tooltip.style('opacity', 0);
          hoverLine.style('opacity', 0);
          hoverDot.style('opacity', 0);
        });
    };

    draw();
    return () => resizeObserver.disconnect();
  }, [data, dimensions]);

  return (
    <div ref={containerRef} className="bg-white shadow rounded p-4 w-full max-w-4xl">
      <div className="flex justify-between items-start">
        <div>
          <p className="block_head mb-4">Predicted Downtime</p>
          {!loading && (selectedStatus === "at_risk" || selectedStatus === null) && (
            <p className="text-3xl font-bold text-gray-600 text-left percentage_down_text">
              {downtime_percentage ? `${downtime_percentage.toLocaleString()}` : "-"}
            </p>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center min-h-[180px]">
          <svg className="w-6 h-6 text-blue-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"></circle>
            <path className="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z">
            </path>
          </svg>
        </div>
      ) : isRestricted && selectedStatus !== "at_risk" && selectedStatus !== null ? (
        <div className="flex justify-center items-center min-h-[180px]">
          <p className="text-gray-400 text-sm">No Data Available</p>
        </div>
      ) : (
        <svg ref={svgRef} viewBox="0 0 700 130" className="w-full h-auto"></svg>
      )}
    </div>
  );
};

export default WeeklyDowntimeGraph;
